import type { ListData } from '../../../types';
import BookIcon from './icons/BookIcon';

/**
 * Displays item metadata in a consistent format:
 * origin/author (as link) or Playlist badge • time message
 */
const ItemMeta = ({ item }: { item: ListData }) => {
  // Use author if available, otherwise calculate domain from URL
  const origin =
    item.type === 'content'
      ? item.author ||
        (item.sourceUrl
          ? (() => {
              try {
                const url = new URL(item.sourceUrl);
                return url.hostname.replace('www.', '');
              } catch {
                return '';
              }
            })()
          : '')
      : '';

  // Calculate time/progress message
  const timeMessage = (() => {
    if (item.type === 'playlist') {
      const contentIds = item.contentIds ? JSON.parse(item.contentIds) : [];
      const position = item.playlistPosition ?? 0;
      const itemsLeft = contentIds.length - position;
      if (itemsLeft <= 0) return 'Done';
      const suffix = position > 0 ? ' left' : '';
      return `${itemsLeft} item${itemsLeft !== 1 ? 's' : ''}${suffix}`;
    }
    const listened = item.secondsListened || 0;
    const left = Math.floor(((item.secondsTotal ?? 0) - listened) / 60);
    if (item.status === 'pending') return 'Loading article\u2026';
    if (left <= 0) return 'Done';
    return `${left} min${listened > 0 ? ' left' : ''}`;
  })();

  const PlaylistBadge = () => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '0.75rem',
        fontStyle: 'normal',
        color: 'var(--teal)',
        background: 'var(--teal-soft)',
        borderRadius: '4px',
        padding: '2px 6px 2px 5px',
        verticalAlign: 'middle',
      }}
    >
      <BookIcon size={11} color="var(--teal)" />
      Playlist
    </span>
  );

  const OriginLink = () =>
    origin ? (
      item.sourceUrl ? (
        <a
          href={item.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--primary-soft)',
            textDecoration: 'underline',
            whiteSpace: 'nowrap',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {origin}
        </a>
      ) : (
        <span style={{ whiteSpace: 'nowrap' }}>{origin}</span>
      )
    ) : null;

  return (
    <p className="subtle-text" style={{ margin: 0 }}>
      {item.type === 'playlist' ? (
        <>
          <PlaylistBadge /> • {timeMessage}
        </>
      ) : (
        <>
          {origin && (
            <>
              <OriginLink /> •{' '}
            </>
          )}
          {timeMessage}
        </>
      )}
    </p>
  );
};

export default ItemMeta;
